import { useMemoizedFn } from 'ahooks';
import { useAuthorization } from 'components/Authorization';
import { updateConversationV2, getTalkInfoWithPhone } from 'services/api/conversation.convList';
import { useSetConversationCategory } from './useConversationCategory';
import { addFakeConversationOrderSn, useSetCurrentConversationIdValue } from '../conversation';
import { useConversationRead } from '../useConversationRead';
import { useUpdateConversationOfId } from './useConversationOfId';
import { isIdEqual } from 'utils/is';

export interface OpenConversationOptions {
  category?: 'mine' | 'all';
  orderSn?: string;
  sourceCode?: string;
}

const normalizeOpenConversationOptions = (
  orderSnOrOptions?: string | OpenConversationOptions,
  sourceCode?: string,
): Required<Pick<OpenConversationOptions, 'category'>> &
  Omit<OpenConversationOptions, 'category'> & { categoryExplicit: boolean } => {
  if (typeof orderSnOrOptions === 'object' && orderSnOrOptions !== null) {
    return {
      category: orderSnOrOptions.category ?? 'mine',
      orderSn: orderSnOrOptions.orderSn,
      sourceCode: orderSnOrOptions.sourceCode,
      categoryExplicit: orderSnOrOptions.category !== undefined,
    };
  }

  return {
    category: 'mine',
    orderSn: orderSnOrOptions,
    sourceCode,
    categoryExplicit: false,
  };
};

export const useConversationOrder = () => {
  return useMemoizedFn(async (conversation: Conversation.Conversation, orderSn?: string) => {
    if (orderSn && conversation.channel === 'SHOPEE' && conversation.mockExternal) {
      addFakeConversationOrderSn(conversation.conversationId, orderSn);
    }
  });
};

// 打开会话窗口
export const useOpenConversation = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const setCurrentConversationIdValue = useSetCurrentConversationIdValue();
  const setConversationCategory = useSetConversationCategory();
  // 已读
  const conversationRead = useConversationRead();
  const updateConversationOfId = useUpdateConversationOfId();
  const conversationOrder = useConversationOrder();

  return useMemoizedFn(
    async (
      conversation: Conversation.Conversation,
      orderSnOrOptions?: string | OpenConversationOptions,
      sourceCode?: string,
    ) => {
      const {
        category = 'mine',
        orderSn,
        sourceCode: normalizedSourceCode,
        categoryExplicit,
      } = normalizeOpenConversationOptions(orderSnOrOptions, sourceCode);
      const { conversationId, supervisePower } = conversation;
      const isReadonly = category === 'all';

      if (categoryExplicit) {
        setConversationCategory(category);
      }
      setCurrentConversationIdValue(conversationId);

      conversationOrder(conversation, orderSn);

      if (isReadonly) {
        try {
          const newConversation = await getTalkInfoWithPhone({
            conversationId,
            needPhone: true,
            suTalkMode: isReadonly,
          });
          updateConversationOfId(
            {
              ...newConversation,
              localError: undefined,
            },
            conversationId,
          );
        } catch (err: any) {
          // All Tab 下存在没有会话的情况，需要特殊处理
          const talk = err?.response?.data?.data as any;
          if (talk?.talkId === '0') {
            updateConversationOfId(
              {
                ...conversation,
                supervisePower: false,
                controlStatus: false,
              },
              conversationId,
            );
          }
        }
        return;
      }

      // TODO: 魔法门进入可能不需要标记已读
      await conversationRead(conversation);

      if (supervisePower) {
        // 管理员不需要抢会话权
        try {
          const newConversation = await getTalkInfoWithPhone({
            conversationId,
            needPhone: true,
            suTalkMode: isReadonly,
          });
          updateConversationOfId(newConversation, conversationId);
        } catch (err) {}
      } else {
        try {
          // 抢会话控制权
          const res = await updateConversationV2({
            conversationId: conversationId,
            clearMark: true,
            acquireControl: true,
            ...(normalizedSourceCode ? { sourceCode: normalizedSourceCode } : {}),
          });

          try {
            const newConversation = await getTalkInfoWithPhone({
              conversationId,
              needPhone: true,
              suTalkMode: isReadonly,
            });
            const { channel, canBeSent, controlStatus } = newConversation;
            const newControlStatus = channel === 'LAZADA' && !canBeSent ? false : controlStatus;
            updateConversationOfId(
              {
                ...newConversation,
                localError: undefined,
                controlStatus: newControlStatus,
              },
              conversationId,
            );
          } catch (err) {
            const { receptionId, receptionName } = res;
            const controlStatus = isIdEqual(payload?.id, receptionId);
            updateConversationOfId(
              {
                controlStatus,
                receptionName: controlStatus ? '' : receptionName,
                receptionId,
                transferMark: false,
                superviseMark: false,
                botMark: false,
                localError: undefined,
              },
              conversationId,
            );
          }
        } catch (err) {
          updateConversationOfId(
            {
              localError: 'API_ACQUIRE_CONTROL_ERROR',
            },
            conversationId,
          );
        }
      }
    },
  );
};
